import { useEffect, useState } from "react";
import { Download, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { FileTypeIcon, fmtBytes } from "./docUtils";

const IMAGE_EXT = ["png", "jpg", "jpeg"];
const TEXT_EXT = ["txt", "csv", "md"];
export const previewKind = (filename) => {
  const ext = (filename || "").split(".").pop().toLowerCase();
  if (ext === "pdf") return "pdf";
  if (IMAGE_EXT.includes(ext)) return "image";
  if (TEXT_EXT.includes(ext)) return "text";
  return null;
};

// Groot voorbeeldvenster voor PDF / afbeelding / tekst; haalt het bestand geauthenticeerd op als blob.
export default function DocumentPreviewDialog({ doc, clubId, onClose, onDownload }) {
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const kind = doc ? previewKind(doc.filename) : null;

  useEffect(() => {
    if (!doc) return undefined;
    let objectUrl = "";
    let alive = true;
    setLoading(true); setText(""); setUrl("");
    api.get(`/clubs/${clubId}/documents/${doc.id}/download`, { responseType: "blob", timeout: 180000 })
      .then(async (res) => {
        if (!alive) return;
        if (kind === "text") setText((await res.data.text()).slice(0, 200000));
        else {
          const blob = kind === "pdf" ? new Blob([res.data], { type: "application/pdf" }) : res.data;
          objectUrl = URL.createObjectURL(blob);
          setUrl(objectUrl);
        }
      })
      .catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); onClose(); })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [doc, clubId, kind, onClose]);

  return (
    <Dialog open={Boolean(doc)} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent aria-describedby={undefined} className="max-w-5xl w-[96vw] p-0 border-2 border-foreground heavy-shadow rounded-none gap-0 [&>button]:hidden" data-testid="doc-preview-dialog">
        {doc && (
          <>
            <div className="flex items-center gap-3 px-4 py-3 border-b-2 border-foreground">
              <FileTypeIcon filename={doc.filename} className="w-5 h-5 shrink-0" />
              <div className="min-w-0 flex-1">
                <DialogTitle className="font-display font-black uppercase tracking-tight text-base truncate" data-testid="doc-preview-title">{doc.filename}</DialogTitle>
                <div className="text-[11px] text-muted-foreground truncate">{doc.label ? `${doc.label} · ` : ""}{fmtBytes(doc.size)} · {doc.uploaded_by_name}</div>
              </div>
              <button type="button" onClick={() => onDownload(doc)} data-testid="doc-preview-download"
                className="p-1.5 border-2 border-foreground hover:bg-foreground hover:text-background transition-colors" aria-label="Downloaden"><Download className="w-4 h-4" /></button>
              <button type="button" onClick={onClose} data-testid="doc-preview-close"
                className="p-1.5 border-2 border-foreground hover:bg-destructive hover:border-destructive hover:text-background transition-colors" aria-label="Sluiten"><X className="w-4 h-4" /></button>
            </div>
            <div className="bg-muted/40 min-h-[40vh] max-h-[80vh] overflow-auto flex items-center justify-center" data-testid="doc-preview-body">
              {loading && <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />}
              {!loading && kind === "pdf" && url && (
                <object data={url} type="application/pdf" className="w-full h-[80vh] bg-white" data-testid="doc-preview-pdf" aria-label={doc.filename}>
                  <div className="p-8 text-center text-sm space-y-3" data-testid="doc-preview-pdf-fallback">
                    <p>Je browser toont geen PDF's in de app. Download het bestand om het te openen.</p>
                    <button type="button" onClick={() => onDownload(doc)} className="btn-brutal bg-primary text-primary-foreground px-3 py-2 font-display uppercase font-bold text-sm inline-flex items-center gap-2"><Download className="w-4 h-4" /> Downloaden</button>
                  </div>
                </object>
              )}
              {!loading && kind === "image" && url && <img src={url} alt={doc.filename} className="max-w-full max-h-[80vh] object-contain" data-testid="doc-preview-image" />}
              {!loading && kind === "text" && <pre className="w-full p-4 text-xs font-mono whitespace-pre-wrap break-words bg-background" data-testid="doc-preview-text">{text || "(leeg bestand)"}</pre>}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
